'use client';

import { FormValidator, useForm } from '@/hooks/useForm';
import { TranslationsTypes, useTranslation } from '@/lib/i18n/i18n.client';

import Button from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { InputDropdown } from '@/components/atoms/inputDropdown';
import LinkButton from '@/components/atoms/linkButton';
import { Text } from '@/components/atoms/text';
import { TextArea } from '@/components/atoms/textArea';

import { INFO_MAX_LENGTH } from '@/features/jobApplicationForm/constants';
import { disallowedEmailDomains } from '@/utils/validation';

import { contactChannels, phoneCodeOptions, phoneCodes } from './constants';

export interface ContactSalesFormSchema {
  companyEmail?: string;
  companyName?: string;
  countryCode: string;
  companyPhone?: string;
  contactChannel: (typeof contactChannels)[number];
  contactChannelId?: string;
  fullName?: string;
  useCase?: string;
  languageCode?: string;
}
const createValidator = (
  texts: TranslationsTypes
): FormValidator<ContactSalesFormSchema> => ({
  fullName: (value) => {
    if (!value) return texts.contact_sales.form.fields_errors.full_name;
  },
  companyEmail: (value) => {
    if (!value) return texts.contact_sales.form.fields_errors.company_email;
    if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value))
      return texts.contact_sales.form.fields_errors.invalid_email;
    if (disallowedEmailDomains.some((domain) => value.includes(`@${domain}`))) {
      return texts.contact_sales.form.fields_errors.invalid_email_domain;
    }
  },
  companyName: (value) => {
    if (!value) return texts.contact_sales.form.fields_errors.company_name;
  },
  useCase: (value) => {
    if (!value) return texts.contact_sales.form.fields_errors.use_case;
    if (value.length > 500)
      return texts.contact_sales.form.fields_errors.use_case_too_long;
  },
  countryCode: (value) => {
    if (
      value &&
      !/^\+\d{1,3}$/.test(value) &&
      !phoneCodes.includes(value as unknown as (typeof phoneCodes)[number])
    )
      return texts.contact_sales.form.fields_errors.country_code;
  },
  companyPhone: (value) => {
    if (value && !/^\d{6,}$/.test(value))
      return texts.contact_sales.form.fields_errors.company_phone;
  },
});

export function ContactSalesFormFields(props: {
  onSubmit: (data: ContactSalesFormSchema) => void;
  loading?: boolean;
}) {
  const { texts } = useTranslation();
  const validator = createValidator(texts);

  const handleFormSubmit = (data: ContactSalesFormSchema) => {
    const formattedData = {
      ...data,
      companyPhone: data.companyPhone
        ? data.countryCode + data.companyPhone
        : '',
    };

    props.onSubmit(formattedData);
  };

  const {
    handleSubmit,
    getFieldProps,
    setFieldValue,
    isValid: isFormValid,
    values,
  } = useForm<ContactSalesFormSchema>({
    initialValues: {
      fullName: '',
      companyName: '',
      companyEmail: '',
      contactChannel: 'Skype',
      contactChannelId: '',
      countryCode: '+1',
      companyPhone: '',
      useCase: '',
      languageCode: '',
    },
    validator,
    validateOnBlur: true,
    validateOnChange: true,
  });

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      className='flex flex-col gap-2'
      data-tracker-component='form'
    >
      <Input
        label={texts.contact_sales.form.fields.full_name}
        isRequired
        {...getFieldProps('fullName')}
      />
      <Input
        label={texts.contact_sales.form.fields.company_name}
        isRequired
        {...getFieldProps('companyName')}
      />
      <Input
        label={texts.contact_sales.form.fields.company_email}
        isRequired
        {...getFieldProps('companyEmail')}
      />
      <div className='flex flex-row items-end gap-6'>
        <InputDropdown
          popoverProps={{
            triggerProps: {
              'data-tracker-context': 'contact_channel',
            },
          }}
          label={texts.contact_sales.form.fields.social_media}
          options={contactChannels.map((channel) => ({
            label: channel,
            value: channel,
          }))}
          {...getFieldProps('contactChannel')}
          onOptionChange={(value) => setFieldValue('contactChannel', value)}
        />

        <Input
          {...getFieldProps('contactChannelId')}
          classNames={{ root: 'w-full' }}
        />
      </div>

      <div className='flex flex-row items-end gap-6'>
        <InputDropdown
          label={texts.contact_sales.form.fields.country_code}
          classNames={{ root: 'max-w-[140px]', dropdown: 'max-h-[200px]' }}
          {...getFieldProps('countryCode')}
          options={phoneCodeOptions}
          onOptionChange={(value) => setFieldValue('countryCode', value)}
          popoverProps={{
            triggerProps: {
              'data-tracker-context': 'country_code',
            },
          }}
        />
        <Input
          label={texts.contact_sales.form.fields.phone_number}
          {...getFieldProps('companyPhone')}
          classNames={{ root: 'w-full' }}
        />
      </div>

      <TextArea
        {...getFieldProps('useCase')}
        maxLength={500}
        data-tracker-context='use_case'
      >
        {values.useCase?.length} / {INFO_MAX_LENGTH}
      </TextArea>
      <div
        className='mt-3 flex flex-col gap-6 md:flex-row'
        data-tracker-component='footer'
      >
        <Text variant='body-s' color='neutral-N50'>
          {texts.contact_sales.form.process_data}{' '}
          <LinkButton
            href='/legal'
            variant='text'
            slim
            classNames={{ root: 'text-body-s' }}
          >
            {texts.general.privacy_policy}.
          </LinkButton>
        </Text>
        <Button
          type='submit'
          classNames={{ root: 'md:w-auto w-full' }}
          disabled={props.loading || !isFormValid}
        >
          {texts.contact_sales.form.ask_expert}
        </Button>
      </div>
    </form>
  );
}
